import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const CHUNK_RETRY_KEY = 'vf_chunk_reload_attempted';

const isChunkLoadError = (error) => {
  const message = error?.message || '';
  return message.includes('Loading chunk') || message.includes('ChunkLoadError');
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, chunkError: false };
    this.handleReload = this.handleReload.bind(this);
    this.handleHardReload = this.handleHardReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, chunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RETRY_KEY)) {
      sessionStorage.setItem(CHUNK_RETRY_KEY, '1');
      this.handleHardReload();
    }
  }

  handleReload() {
    this.setState({ hasError: false, error: null, chunkError: false });
    window.location.reload();
  }

  async handleHardReload() {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
      if (window.caches?.keys) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((key) => window.caches.delete(key)));
      }
    } catch {
      // Best-effort cache cleanup; reload regardless.
    }
    this.setState({ hasError: false, error: null, chunkError: false });
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.chunkError;
      const title = isChunkError ? 'Update needed' : 'Something went wrong';
      const message = isChunkError
        ? 'We updated the app, but your browser still has an older bundle. A hard reload will fetch the latest files.'
        : (this.state.error?.message || 'An unexpected error occurred.');
      const buttonLabel = isChunkError ? 'Hard Reload' : 'Reload Page';
      const onReload = isChunkError ? this.handleHardReload : this.handleReload;

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" role="alert" aria-live="assertive">
          <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-[#323338] mb-2">{title}</h2>
          <p className="text-sm text-[#676879] mb-4 max-w-md">{message}</p>
          <Button
            size="sm"
            onClick={onReload}
            aria-label={buttonLabel}
          >
            <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" /> {buttonLabel}
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
